import type { ChatCardItem, ChatMessage } from '@karehero/models';

import {
  BEAT_STEPS,
  questionStep,
  TALK_STEPS,
  type BeatStep,
} from 'config/onboardingV4/beatSteps';
import * as copy from 'config/onboardingV4/copy';
import type {
  OnboardingV4Beat,
  OnboardingV4Flow,
} from 'config/onboardingV4/flows';
import type { TriageQuestion } from 'config/triageQuestions';
import { questionsLeft, type Knowledge } from 'utils/triageKnowledge';
import { card, menu, say } from 'utils/chatMessages';
import {
  choiceStyleFor,
  interjectionFor,
  startOptions,
  type Interjection,
} from 'utils/onboardingV4Steps';
import { helpLines } from 'utils/onboardingV4Help';
import {
  heardKey,
  heardRead,
  isHelpWithAskedIn,
  talkReopened,
  under18Key,
  under18Lifted,
  type OnboardingV4Progress,
  type OnboardingV4Stage,
  type ProgressChange,
} from 'utils/onboardingV4Progress';
import { phrase, type Person } from 'utils/person';

// What Keni does next at each beat: her lines, and the move that follows
// them. Pure, so a conversation can be walked without rendering it.

/** Marks the opening line of the questions as said. */
export const QUESTIONS_INTRO = 'questions:intro';

/** The summary of what they said, as the heard beat sees it. */
export interface SummaryScene {
  status: 'reading' | 'read' | 'incomplete';
  lines: readonly string[];
  unconfirmedID?: string;
  /** Read, and newer than the one read back before. */
  isReady: boolean;
  /** Waited for longer than we will. */
  isLate: boolean;
  /** Whether its wait has been started this round. */
  isWaitedFor: boolean;
}

export interface Scene {
  flow: OnboardingV4Flow;
  progress: OnboardingV4Progress;
  beat: OnboardingV4Beat | null;
  person: Person;
  questions: readonly TriageQuestion[];
  /** The question being asked, while the beat is the questions. */
  question: TriageQuestion | null;
  knowledge: Knowledge;
  /** Whether what they share may be read. */
  canRead: boolean;
  /** Whether they can talk, so the opening says so. */
  isVoiceOn: boolean;
  /** Whether what they said is still being read. */
  isReading: boolean;
  /** The reading checklist, as it stands. */
  reading: ChatCardItem[];
  /** What we would need, as listed. */
  needLabels: string[];
  /** Their phone number is read already, so it is not asked. */
  isPhoneKnown: boolean;
  summary: SummaryScene;
}

/** A move on from a beat, applied once Keni's lines have landed. */
export type Transition =
  | { kind: 'asked'; ids: string[] }
  | {
      kind: 'finished';
      beatID: string;
      changes?: Partial<OnboardingV4Progress>;
    }
  | { kind: 'changed'; change: ProgressChange }
  | { kind: 'staged'; stage: OnboardingV4Stage };

export type Action =
  /** Keni says her lines, then moves on. */
  | { kind: 'say'; lines: ChatMessage[]; then?: Transition }
  /** Moves on without a word. */
  | { kind: 'move'; then: Transition }
  /** Starts the clock on the summary. */
  | { kind: 'waitForSummary' };

const asked = (...ids: string[]): Transition => ({ kind: 'asked', ids });
const finished = (
  beatID: string,
  changes?: Partial<OnboardingV4Progress>,
): Transition => ({ kind: 'finished', beatID, changes });
const speak = (lines: ChatMessage[], then?: Transition): Action => ({
  kind: 'say',
  lines,
  then,
});
const move = (then: Transition): Action => ({ kind: 'move', then });

/** Keni's question: a menu where its answers are attached to it, else a line with them offered apart. */
const askLine = (
  { beat, flow }: Scene,
  step: Pick<BeatStep, 'ask' | 'options' | 'choiceStyle'>,
): ChatMessage =>
  choiceStyleFor(beat, flow, step) === 'attached' && step.options?.length
    ? menu(step.ask, step.options)
    : say(step.ask);

const enterTalk = ({ flow, person, isVoiceOn }: Scene): Action => {
  if (flow.talkOpening === 'open') {
    return speak(
      [
        say(TALK_STEPS.open.ask),
        card('prompts', copy.OPENING.promptsTitle, copy.OPENING.prompts, {
          items: copy.OPENING.prompts.map((label) => ({
            label,
            state: 'waiting',
          })),
        }),
      ],
      asked('talk'),
    );
  }
  const prompts = person.isSelf
    ? copy.TALK_PROMPTS.self
    : copy.TALK_PROMPTS.other;
  return speak(
    [
      say(phrase(copy.TALK_PREAMBLE, person)),
      card('prompts', copy.TALK_PROMPTS.title, prompts),
      say(TALK_STEPS.prompted.ask),
      ...(isVoiceOn ? [say(copy.TALK.recordHint[flow.voiceControl])] : []),
    ],
    asked('talk'),
  );
};

/** Said before the questions the talk left unanswered, so they know how many. */
const questionsLeftLine = (person: Person, left: number) =>
  phrase(left === 1 ? copy.QUESTIONS.oneLeft : copy.QUESTIONS.someLeft, person);

/** Said once the reading is done, before any questions it left. */
const afterReading = (person: Person, left: number) => {
  const next =
    left === 0 ? copy.READING.allTold : questionsLeftLine(person, left);
  return `${copy.READING.thanks} ${next}`;
};

const leaveTalk = (scene: Scene): Action => {
  const { flow, progress, person, questions, knowledge, canRead } = scene;
  const next = flow.beats[flow.beats.findIndex((b) => b.id === 'talk') + 1];
  const left =
    next?.kind === 'questions' && progress.who !== 'exploring'
      ? questionsLeft(questions, next.mode, knowledge)
      : 0;
  const lines =
    canRead && flow.talkWaitsFor !== 'summary'
      ? [say(afterReading(person, left))]
      : [];
  // The work done stays in the chat to be read, as a checklist.
  const work =
    canRead && flow.readingStyle === 'checklist'
      ? [card('reading', copy.READING.cardTitle, [], { items: scene.reading })]
      : [];
  return speak([...work, ...lines], finished('talk'));
};

/** The line before the first question: why, where they are asked before the talk; what is left, where the summary came first. */
const questionsIntro = ({ flow, progress, person }: Scene): ChatMessage[] => {
  if (!progress.done.includes('talk')) {
    return [say(phrase(copy.QUESTIONS.intro, person))];
  }
  // Leaving the talk says how many are left, unless the summary is waited for instead.
  return flow.talkWaitsFor === 'summary'
    ? [say(copy.QUESTIONS.afterSummary)]
    : [];
};

const askQuestion = (scene: Scene, question: TriageQuestion): Action => {
  const isFirst = !scene.progress.asked.includes(QUESTIONS_INTRO);
  return speak(
    [
      ...(isFirst ? questionsIntro(scene) : []),
      askLine(scene, questionStep(question, scene.person)),
    ],
    asked(...(isFirst ? [QUESTIONS_INTRO] : []), question.id),
  );
};

/** The line before what we heard: first, again after what they need help with, or again after a correction. */
const heardSays = (progress: OnboardingV4Progress) => {
  const { heardRound } = progress;
  if (heardRound === 0) return copy.HEARD.says;
  return isHelpWithAskedIn(progress, heardRound - 1)
    ? copy.HELP_WITH.saysAgain
    : copy.HEARD.saysAgain;
};

const enterHeard = ({ flow, progress, summary }: Scene): Action | undefined => {
  if (!summary.isReady) {
    if (summary.isLate) {
      return speak([say(copy.HEARD.stillReading)], finished('heard'));
    }
    return summary.isWaitedFor ? undefined : { kind: 'waitForSummary' };
  }
  const isPartial = summary.status === 'incomplete';
  const lines = [...summary.lines];
  // Nothing could be made out: they are asked to tell us again, once.
  if (!lines.length && !isPartial) {
    return progress.isRetold
      ? move(finished('heard'))
      : speak([say(copy.STATES.notUnderstood)], {
          kind: 'changed',
          change: talkReopened(),
        });
  }
  const round = progress.heardRound;
  // The 2.0 design asks with the card's own title, so says nothing after it.
  const isOpen = flow.talkOpening === 'open';
  return speak(
    [
      say(heardSays(progress)),
      card(
        'heard',
        isPartial ? copy.HEARD.partialTitle : copy.HEARD.title,
        isPartial ? [...lines, copy.HEARD.partialNote] : lines,
        { isEditable: !isPartial },
      ),
      ...(isOpen ? [] : [say(BEAT_STEPS.heard.ask)]),
    ],
    { kind: 'changed', change: heardRead(round, summary.unconfirmedID) },
  );
};

const enterHelp = ({ person, knowledge }: Scene, asks: boolean): Action =>
  speak(
    [
      say(copy.HELP.says),
      card('help', copy.HELP.title, helpLines(person, knowledge)),
      ...(asks ? [say(BEAT_STEPS.help.ask)] : []),
    ],
    asks ? asked('help') : finished('help'),
  );

// Its items are worked out as it is shown, so they tick as facts land.
const enterNeed = ({ needLabels }: Scene): Action =>
  speak(
    [
      say(copy.NEED.says),
      card('need', copy.NEED.title, needLabels),
      say(BEAT_STEPS.need.ask),
    ],
    asked('need'),
  );

/** What cuts in ahead of the beat, said once by its key; the stop waits for what they said to be read. */
const interject = (
  scene: Scene,
  { kind, key, step }: Interjection,
): Action | undefined => {
  if (kind === 'stop' && scene.isReading) return undefined;
  if (scene.progress.asked.includes(key)) return undefined;
  return speak([askLine(scene, step)], asked(key));
};

/** What Keni does next: says a beat's lines once it is current, and moves on once nothing is left in it to answer. */
export const nextAction = (scene: Scene): Action | undefined => {
  const { beat, progress, question, canRead, isReading } = scene;
  if (!beat) return move({ kind: 'staged', stage: 'confirm' });
  const isAsked = (id: string) => progress.asked.includes(id);
  const onceAsked = (id: string, lines: ChatMessage[]) =>
    isAsked(id) ? undefined : speak(lines, asked(id));
  const { knowledge, person } = scene;
  const interjection = interjectionFor({ beat, knowledge, progress, person });
  if (interjection) return interject(scene, interjection);
  // The stop lifted by an age read since rather than changed: said afresh
  // should the age come back under 18.
  if (isAsked(under18Key(progress))) {
    return move({ kind: 'changed', change: under18Lifted() });
  }
  switch (beat.kind) {
    case 'consent':
      return scene.flow.consentStyle === 'chat'
        ? onceAsked('ai-consent', [askLine(scene, BEAT_STEPS.consent)])
        : undefined;
    case 'who':
      return onceAsked('who', [askLine(scene, BEAT_STEPS.who)]);
    case 'talk':
      if (!isAsked('talk')) return enterTalk(scene);
      return progress.readingFrom && !isReading ? leaveTalk(scene) : undefined;
    case 'questions':
      if (isReading) return undefined;
      if (!question) return move(finished('questions'));
      return isAsked(question.id) ? undefined : askQuestion(scene, question);
    case 'heard':
      if (!canRead) return move(finished('heard'));
      return isAsked(heardKey(progress.heardRound))
        ? undefined
        : enterHeard(scene);
    case 'help':
      if (!beat.asks) return enterHelp(scene, false);
      return isAsked('help') ? undefined : enterHelp(scene, true);
    case 'need':
      return isAsked('need') ? undefined : enterNeed(scene);
    case 'gather':
      return onceAsked('gather', [say(BEAT_STEPS.gather.ask)]);
    case 'phone':
      if (isAsked('phone')) return undefined;
      return scene.isPhoneKnown
        ? move(finished('phone'))
        : speak([say(BEAT_STEPS.phone.ask)], asked('phone'));
    case 'start':
      return onceAsked('start', [
        askLine(scene, {
          ...BEAT_STEPS.start,
          options: startOptions(beat, scene.knowledge, scene.person),
        }),
      ]);
    default:
      return undefined;
  }
};
